import { useEffect, useRef } from 'react';
import { type GitlabProvisioningManageResult } from 'network/httpClients/GitlabClient';
import {
  resolveInstallStartErrorKey,
  useGitlabManageRun,
  type ManageRunOutcome,
} from '../manageRun';
import { type GitlabV2GroupInstallPartition } from '../types';
import { getInstallErrorKey } from '../utils';

/** One top-level group's install progress, reported up to the merged results list. */
export type GroupInstallReport = {
  results: GitlabProvisioningManageResult[];
  outcome: ManageRunOutcome;
};

type GroupInstallRunnerProps = {
  /** This group's slice of the selection (the scopes to install under it). */
  partition: GitlabV2GroupInstallPartition;
  /** The group's provisioned installation id (rendered only once provisioned). */
  installationId: string;
  /** Transient elevation PAT, re-sent per `/manage` call. */
  accessToken: string;
  /** Streams this group's per-scope results + outcome up to the parent. */
  onReport: (topLevelGroupId: number, report: GroupInstallReport) => void;
};

/**
 * Headless runner for one top-level group's install: fires the add-only `/manage` for
 * that group's installation and streams the per-scope results + outcome up. Rendered only
 * once the group is provisioned, one per group, so the parent can render each group's
 * results under its own "{{group}} installation" section. Renders nothing itself.
 */
export const GroupInstallRunner = ({
  partition,
  installationId,
  accessToken,
  onReport,
}: GroupInstallRunnerProps) => {
  const { run, results, outcome } = useGitlabManageRun(
    { installationId, accessToken },
    resolveInstallStartErrorKey(getInstallErrorKey),
  );

  const hasRunRef = useRef(false);

  useEffect(() => {
    if (hasRunRef.current) return;

    hasRunRef.current = true;
    run({ add: partition.add, remove: { groupIds: [], projectIds: [] } });
  }, [run, partition]);

  useEffect(() => {
    onReport(partition.topLevelGroupId, { results, outcome });
  }, [results, outcome, partition.topLevelGroupId, onReport]);

  return null;
};
